import { useState, useEffect, Fragment, useCallback } from "react";
import axios from "axios";
import Footer from "../../Footer";
import Navbar from "../../Navbar";
import OutletSearch from "../../SelectSearch/OutletSearch";
import { MdAutorenew, MdError } from "react-icons/md";
import { FaChevronDown, FaChevronUp, FaTrash } from "react-icons/fa6";
import { formatSalesValue } from './../../../utils/formatUtils';
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEdit } from "react-icons/fa";
import { convertToDisplayUnit } from "../../../utils/quantityConversion";
import RenameRecipeModal from "./RenameRecipeModal";
import { selectStyles } from "../../../utils/cssUtils";

const ViewRecipeItems = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");
    const navigate = useNavigate();

    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [recipes, setRecipes] = useState([]);
    const [foodMenuItems, setFoodMenuItems] = useState([]);
    const [beverageMenuItems, setBeverageMenuItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedRow, setExpandedRow] = useState(null);
    const [error, setError] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [renamingRecipe, setRenamingRecipe] = useState(null);
    const [printMenuOpenType, setPrintMenuOpenType] = useState(null);

    const toggleRow = (id) => {
        setExpandedRow(prev => (prev === id ? null : id));
    };

    const fetchRecipes = useCallback(async () => {
        if (!selectedOutlet || !token) return;

        try {
            setLoading(true);
            setError("");

            setSelectedItem(null);
            setSelectedGroup(null);
            setRecipes([]);
            setExpandedRow(null);
            setRenamingRecipe(null);

            const res = await axios.get(
                `${baseUrl}/recipes?outlet=${encodeURIComponent(selectedOutlet)}`
            );

            setRecipes(res.data || []);
        } catch (err) {
            console.error(err);
            setError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Error fetching data"
            );
        } finally {
            setLoading(false);
        }
    }, [baseUrl, selectedOutlet, token]);

    const fetchMenuLists = useCallback(async () => {
        if (!selectedOutlet || !token) return;

        try {
            const [foodRes, beverageRes] = await Promise.all([
                axios.post(`${baseUrl}/menu-by-type`, {
                    outlet: selectedOutlet,
                    token,
                    type: "Food",
                }),
                axios.post(`${baseUrl}/menu-by-type`, {
                    outlet: selectedOutlet,
                    token,
                    type: "Beverage",
                }),
            ]);

            setFoodMenuItems(foodRes.data?.[0]?.items || []);
            setBeverageMenuItems(beverageRes.data?.[0]?.items || []);
        } catch (err) {
            console.error(err);
            toast.error(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Failed to fetch menu items"
            );
        }
    }, [baseUrl, selectedOutlet, token]);

    const handleEdit = async (recipe) => {
        if (!selectedOutlet || !token) return;

        setLoading(true);
        try {
            const response = await axios.post(`${baseUrl}/recipes`, {
                token: token,
                item_name: recipe.name,
                outlet: selectedOutlet
            });

            const data = response.data || [];

            if (data || data?.items)
                navigate(`/menuitemcost?outlet=${selectedOutlet}`, {
                    state: {
                        recipeData: data[0]
                    }
                });

            setLoading(false);
        } catch (err) {
            toast.error(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Failed to navigate to recipe page");

        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRecipes();
        fetchMenuLists();
    }, [fetchRecipes, fetchMenuLists]);

    const recipeOptions = recipes.map(r => ({
        value: r.id,
        label: r.name
    }));

    const groupOptions = [
        ...new Map(
            recipes
                .filter(r => r.GroupName)
                .map(r => [
                    r.GroupName,
                    {
                        value: r.GroupName,
                        label: r.GroupName
                    }
                ])
        ).values()
    ];

    const filteredRecipes = recipes.filter((recipe) => {
        const recipeMatch = selectedItem
            ? recipe.id === selectedItem.value
            : true;

        const groupMatch = selectedGroup
            ? recipe.GroupName === selectedGroup.value
            : true;

        return recipeMatch && groupMatch;
    });

    const groupedRecipes = filteredRecipes.reduce((acc, recipe) => {
        const type = recipe.ItemType || "Others";

        if (!acc[type]) acc[type] = [];
        acc[type].push(recipe);

        return acc;
    }, {});

    // Sort helper: groups by group name ascending, recipes within a group by name ascending.
    // Used for BOTH the on-screen table and the print output so ordering stays consistent.
    const getSortedGroups = (typeRecipes) => {
        const groupedByName = typeRecipes.reduce((acc, recipe) => {
            const group = recipe.GroupName || "Ungrouped";
            if (!acc[group]) acc[group] = [];
            acc[group].push(recipe);
            return acc;
        }, {});

        return Object.entries(groupedByName)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([groupName, groupRecipes]) => [
                groupName,
                [...groupRecipes].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
            ]);
    };

    const updateConfirmation = (recipe, newName) => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to update this recipe?</p>

                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();

                            try {
                                const payload = {
                                    id: recipe.id,
                                    name: newName
                                };

                                await axios.put(`${baseUrl}/recipe/update-name`, payload);
                                toast.success("Recipe name updated successfully!");
                                setRenamingRecipe(null);
                                fetchRecipes();

                            } catch (err) {
                                console.error(err);
                                toast.error(err?.response?.data?.error || err?.response?.data?.message || "Error updating recipe");
                            }
                        }}
                    >
                        Update
                    </button>

                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
            }
        );
    };

    const handleDelete = (recipeId) => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to delete this recipe?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        onClick={async () => {
                            toast.dismiss('delete-subrecipe-confirmation');
                            setLoading(true);
                            try {
                                await axios.delete(`${baseUrl}/recipe/${recipeId}`, {
                                    data: { token }
                                });
                                toast.success("Recipe deleted successfully");
                                setSelectedItem(null)
                                fetchRecipes();
                            } catch (err) {
                                console.log(err);
                                toast.error("Error deleting recipe")
                            } finally {
                                setLoading(false);
                            }
                        }}
                    >
                        Delete
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss('delete-subrecipe-confirmation')}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'delete-recipe-confirmation'
            }
        );
    };

    const handleCreateNavigate = () => {
        localStorage.removeItem('item');
        navigate(`/menuitemcost?outlet=${selectedOutlet}`);
    }

    const buildDetailRowsHTML = (recipe) => {
        const hasItems = recipe.items?.length > 0;
        const hasSubRecipes = recipe.sub_recipes?.length > 0;

        if (!hasItems && !hasSubRecipes) {
            return `
            <tr>
                <td colspan="4" class="detail-empty">No recipe for this item</td>
            </tr>
        `;
        }

        let rowsHtml = "";

        if (hasItems) {
            rowsHtml += `
            <tr><td colspan="4" class="detail-heading">Ingredients</td></tr>
            <tr class="detail-col-header"><td>Name</td><td>Qty</td><td colspan="2">Cost</td></tr>
        `;

            rowsHtml += recipe.items.map((item) => `
                <tr class="detail-row">
                    <td>‣ ${item.name}</td>
                    <td>${convertToDisplayUnit(item.quantity, item.uom, item.new_uom, item.unit)} ${item.new_uom || item.uom}</td>
                    <td colspan="2">${formatSalesValue(item.cost)}</td>
                </tr>
            `).join("");
        }

        if (hasSubRecipes) {
            rowsHtml += `
            <tr><td colspan="4" class="detail-heading">Sub Recipes</td></tr>
            <tr class="detail-col-header"><td>Name</td><td>Qty</td><td colspan="2">Cost</td></tr>
        `;

            rowsHtml += recipe.sub_recipes.map((sub) => `
                <tr class="detail-row">
                    <td>‣ ${sub.name}</td>
                    <td>${convertToDisplayUnit(sub.quantity, sub.uom, sub.new_uom, sub.unit)} ${sub.new_uom || sub.uom}</td>
                    <td colspan="2">${formatSalesValue(sub.costprice)}</td>
                </tr>
            `).join("");
        }

        return rowsHtml;
    };

    const generatePrintHTML = (type, withRecipe) => {
        const typeRecipes = groupedRecipes[type] || [];
        const sortedGroups = getSortedGroups(typeRecipes);

        let bodyContent;

        if (!withRecipe) {
            const groupSections = sortedGroups.map(([groupName, groupRecipes]) => {
                const rows = groupRecipes.map((recipe) => `
                    <tr>
                        <td>${recipe.name}</td>
                        <td>${formatSalesValue(recipe.costprice)}</td>
                        <td>${formatSalesValue(recipe.sellingprice)}</td>
                        <td>${recipe.cost_percent}%</td>
                    </tr>
                `).join("");

                return `
                    <tr><td colspan="4" class="group-row">${groupName}</td></tr>
                    ${rows}
                `;
            }).join("");

            bodyContent = `
                <table class="main-table">
                    <thead>
                        <tr>
                            <th>Recipe Name</th>
                            <th>Cost Price</th>
                            <th>Selling Price</th>
                            <th>Cost %</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${groupSections}
                    </tbody>
                </table>
            `;
        } else {
            bodyContent = sortedGroups.map(([groupName, groupRecipes]) => {
                const rows = groupRecipes.map((recipe) => {
                    const mainRow = `
                        <tr class="recipe-row">
                            <td>${recipe.name}</td>
                            <td>${formatSalesValue(recipe.costprice)}</td>
                            <td>${formatSalesValue(recipe.sellingprice)}</td>
                            <td>${recipe.cost_percent}%</td>
                        </tr>
                    `;
                    const spacerRow = `<tr class="spacer-row"><td colspan="4"></td></tr>`;
                    return mainRow + buildDetailRowsHTML(recipe) + spacerRow;
                }).join("");

                return `
                    <div class="group-block">
                        <h3>${groupName}</h3>
                        <table class="main-table">
                            <thead>
                                <tr>
                                    <th>Recipe Name</th>
                                    <th>Cost Price</th>
                                    <th>Selling Price</th>
                                    <th>Cost %</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${rows}
                            </tbody>
                        </table>
                    </div>
                `;
            }).join("");
        }

        return `
            <html>
            <head>
                <title>Recipe Print - ${selectedOutlet || ""} - ${type}</title>
                <style>
                    @page {
                        size: auto;
                        margin: 5mm 5mm;
                    }
                    * { box-sizing: border-box; }
                    html, body { margin: 0; }
                    body { font-family: Arial, sans-serif; padding: 4px; color: #111827; }
                    h1 { margin: 0 0 4px; font-size: 16px; }
                    h2 { margin: 0 0 16px; font-size: 14px; font-weight: 600; color: #374151; }
                    h3 { margin: 16px 0 6px; font-size: 14px; }
                    .group-block:first-of-type h3 { margin-top: 0; }
                    table.main-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-bottom: 16px;
                        outline: 1px solid #d1d5db;
                    }
                    table.main-table th, table.main-table td {
                        border: 1px solid #d1d5db;
                        padding: 6px 10px;
                        text-align: center;
                        font-size: 13px;
                    }
                    table.main-table th { background: #f3f4f6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .group-row { text-align: left !important; background: #e5e7eb; font-weight: 600; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    tr.recipe-row td {
                        font-weight: 600;
                        background: #ffffff;
                    }
                    tr.recipe-row td:first-child {
                        border-left: 3px solid #9ca3af;
                    }
                    tr.detail-heading td {
                        text-align: left;
                        font-weight: 600;
                        background: #eef2f7;
                        padding: 4px 10px 4px 20px;
                        font-size: 12px;
                        color: #1f2937;
                        border-left: 3px solid #cbd5e1;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    tr.detail-col-header td {
                        background: #f9fafb;
                        font-weight: 600;
                        font-size: 11px;
                        padding: 3px 8px 3px 20px;
                        color: #1f2937;
                        border-left: 3px solid #cbd5e1;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    tr.detail-row td {
                        font-size: 12px;
                        padding: 3px 8px 3px 20px;
                        color: #1f2937;
                        background: #fcfcfd;
                        border-left: 3px solid #cbd5e1;
                    }
                    tr.detail-row td:first-child {
                        padding-left: 26px;
                    }
                   td.detail-empty {
                        text-align: left;
                        color: #1f2937;
                        padding: 6px 10px 6px 20px;
                        font-size: 12px;
                        font-style: italic;
                        border-left: 3px solid #cbd5e1;
                    }
                    table.sub-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-bottom: 8px;
                        outline: 1px solid #e5e7eb;
                    }
                    table.sub-table th, table.sub-table td {
                        border: 1px solid #e5e7eb;
                        padding: 4px 8px;
                        font-size: 12px;
                        text-align: center;
                    }
                        tr.spacer-row td {
                        padding: 0;
                        height: 16px;
                        border: none;
                        background: transparent;
                    }
                    table.sub-table th { background: #f9fafb; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    @media print {
                        body { padding: 4px; }
                    }
                </style>
            </head>
            <body>
                <h1>${selectedOutlet || ""}</h1>
                <h2>Menu Type: ${type}</h2>
                ${bodyContent}
            </body>
            </html>
        `;
    };

    const handlePrint = (type, withRecipe) => {
        setPrintMenuOpenType(null);

        const typeRecipes = groupedRecipes[type] || [];
        if (typeRecipes.length === 0) {
            toast.error(`No recipes available to print for ${type}`);
            return;
        }

        const printWindow = window.open("", "_blank");
        if (!printWindow) {
            toast.error("Unable to open print window. Please allow pop-ups for this site.");
            return;
        }

        printWindow.document.write(generatePrintHTML(type, withRecipe));
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
        };
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />

            <div className="flex-grow py-3">
                {renamingRecipe && (
                    <RenameRecipeModal
                        recipe={renamingRecipe}
                        foodMenuItems={foodMenuItems}
                        beverageMenuItems={beverageMenuItems}
                        onConfirm={(recipe, newName) => {
                            setRenamingRecipe(null);
                            updateConfirmation(recipe, newName);
                        }}
                        onClose={() => setRenamingRecipe(null)}
                    />
                )}

                <div className="flex justify-center mb-2.5">
                    <OutletSearch
                        selectedOutlet={selectedOutlet}
                        setSelectedOutlet={setSelectedOutlet}
                        token={token}
                        compact={true}
                    />
                </div>

                <div className="max-w-6xl mx-auto w-full mb-3 flex justify-between items-end">
                    <div className="flex items-center gap-4">
                        <div className="w-[300px]">
                            <Select
                                options={groupOptions}
                                value={selectedGroup}
                                onChange={setSelectedGroup}
                                isClearable
                                placeholder="Search Group..."
                                className="z-30"
                                styles={selectStyles}
                            />
                        </div>

                        <div className="w-[400px]">
                            <Select
                                options={
                                    selectedGroup
                                        ? recipeOptions.filter((option) => {
                                            const recipe = recipes.find(
                                                r => r.id === option.value
                                            );

                                            return (
                                                recipe?.GroupName ===
                                                selectedGroup.value
                                            );
                                        })
                                        : recipeOptions
                                }
                                value={selectedItem}
                                onChange={setSelectedItem}
                                isClearable
                                placeholder="Search Recipe..."
                                className="z-30"
                                styles={selectStyles}
                            />
                        </div>
                    </div>

                    <button
                        onClick={handleCreateNavigate}
                        className="px-3 py-1.5 rounded-md text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md whitespace-nowrap"
                    >
                        Create Recipe
                    </button>
                </div>

                {loading ? (
                    <div className="max-w-6xl mx-auto rounded-md bg-blue-50 p-4">
                        <div className="flex gap-3 items-center justify-center">
                            <MdAutorenew className="h-5 w-5 text-blue-300 animate-spin" />
                            <div className="text-blue-700">
                                Loading recipe items...
                            </div>
                        </div>
                    </div>
                ) : error ? (
                    <div className="max-w-6xl mx-auto mt-4 rounded-md bg-red-50 p-4">
                        <div className="flex gap-3 justify-center items-stretch">
                            <MdError className="h-5 w-5 text-red-400" />
                            <div className="text-red-700">{error}</div>
                        </div>
                    </div>
                ) : (
                    <div className="px-12 grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {Object.keys(groupedRecipes || {}).length === 0 ? (
                            <div className="text-center py-10 text-gray-500 text-lg col-span-full">
                                No recipe available
                            </div>
                        ) : (
                            Object.entries(groupedRecipes).map(([type, typeRecipes]) => {
                                const groupEntries = getSortedGroups(typeRecipes);

                                return (
                                    <div
                                        key={type}
                                        className={`border rounded-sm overflow-auto ${selectedItem || selectedGroup ? "col-span-full max-w-6xl mx-auto w-full" : ""
                                            }`}
                                    >

                                        {/* GROUP TITLE + PRINT BUTTON */}
                                        <div className="bg-gray-200 px-4 py-2 font-semibold text-left flex items-center justify-between">
                                            <span>{type}</span>

                                            <div className="relative">
                                                <button
                                                    onClick={() =>
                                                        setPrintMenuOpenType((prev) =>
                                                            prev === type ? null : type
                                                        )
                                                    }
                                                    className="px-2.5 py-1 rounded-md text-xs font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 whitespace-nowrap"
                                                >
                                                    Print
                                                </button>

                                                {printMenuOpenType === type && (
                                                    <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-40">
                                                        <button
                                                            onClick={() => handlePrint(type, true)}
                                                            className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                                        >
                                                            With Recipe
                                                        </button>
                                                        <button
                                                            onClick={() => handlePrint(type, false)}
                                                            className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                                        >
                                                            Without Recipe
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="w-full max-h-[600px] overflow-y-auto tiny-scrollbar">
                                            <table className="min-w-full divide-y divide-gray-200 text-center text-sm">
                                                <thead className="bg-gray-50 top-0 sticky z-20">
                                                    <tr>
                                                        <th></th>
                                                        <th className="px-2 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap">Recipe Name</th>
                                                        <th className="px-2 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap">Cost Price</th>
                                                        <th className="px-2 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap">Selling Price</th>
                                                        <th className="px-2 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider whitespace-nowrap">Cost %</th>
                                                        <th className="px-2 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                                                    </tr>
                                                </thead>

                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {groupEntries.map(([groupName, groupRecipes], gi) => (
                                                        <Fragment key={groupName}>
                                                            {/* GROUP NAME ROW */}
                                                            <tr>
                                                                <td colSpan={6} className="bg-gray-100 text-left px-4 py-1.5 font-semibold text-gray-700">
                                                                    {groupName}
                                                                </td>
                                                            </tr>

                                                            {groupRecipes.map((recipe) => {
                                                                const isExpanded = expandedRow === recipe.id;

                                                                return (
                                                                    <Fragment key={recipe.id}>
                                                                        {/* MAIN ROW */}
                                                                        <tr className="hover:bg-gray-50">
                                                                            <td
                                                                                className="p-2 cursor-pointer"
                                                                                onClick={() => toggleRow(recipe.id)}
                                                                            >
                                                                                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                                                            </td>

                                                                            <td className="px-3 py-2.5 font-medium text-gray-900">
                                                                                <div className="flex items-center justify-center gap-2">
                                                                                    <span onClick={() => toggleRow(recipe.id)} className="cursor-pointer">
                                                                                        {recipe.name}
                                                                                    </span>

                                                                                    <FaEdit
                                                                                        className="text-blue-500 cursor-pointer transition-all duration-200 hover:scale-110 hover:text-blue-700 active:scale-95"
                                                                                        onClick={() => setRenamingRecipe(recipe)}
                                                                                    />
                                                                                </div>
                                                                            </td>

                                                                            <td className="px-3 py-2.5">{formatSalesValue(recipe.costprice)}</td>
                                                                            <td className="px-3 py-2.5">{formatSalesValue(recipe.sellingprice)}</td>
                                                                            <td className="px-3 py-2.5">{recipe.cost_percent}%</td>
                                                                            <td className="px-3 py-2.5">
                                                                                <div className="flex justify-center gap-1.5">
                                                                                    <button
                                                                                        className="p-1.5 rounded border border-gray-300 text-blue-600 hover:bg-blue-50 transition-colors"
                                                                                        onClick={() => handleEdit(recipe)}
                                                                                    >
                                                                                        <FaEdit className="h-3 w-3" />
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => handleDelete(recipe.id)}
                                                                                        className="p-1.5 rounded border border-gray-300 text-red-600 hover:bg-red-50 transition-colors"
                                                                                    >
                                                                                        <FaTrash className="h-3 w-3" />
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>

                                                                        {/* EXPANDED ROW */}
                                                                        {isExpanded && (
                                                                            <tr className="text-sm">
                                                                                <td colSpan={6} className="p-3 bg-gray-50">

                                                                                    {/* INGREDIENTS */}
                                                                                    <div className="text-left font-semibold mb-2 text-base">Ingredients</div>

                                                                                    {recipe.items?.length > 0 ? (
                                                                                        <table className="min-w-full text-sm mb-4">
                                                                                            <thead>
                                                                                                <tr className="bg-gray-100">
                                                                                                    <th className="p-2">Name</th>
                                                                                                    <th className="p-2">Qty</th>
                                                                                                    <th className="p-2">UOM</th>
                                                                                                    <th className="p-2">Cost</th>
                                                                                                    <th className="p-2">Unit</th>
                                                                                                    <th className="p-2">Rate</th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {recipe.items.map((item, i) => (
                                                                                                    <tr key={i} className="border-t">
                                                                                                        <td className="p-2">{item.name}</td>
                                                                                                        <td className="p-2">
                                                                                                            {convertToDisplayUnit(
                                                                                                                item.quantity,
                                                                                                                item.uom,
                                                                                                                item.new_uom,
                                                                                                                item.unit
                                                                                                            )}
                                                                                                        </td>
                                                                                                        <td className="p-2">{item.new_uom || item.uom}</td>
                                                                                                        <td className="p-2">{formatSalesValue(item.cost)}</td>
                                                                                                        <td className="p-2">{item.unit} ({item.uom})</td>
                                                                                                        <td className="p-2">{formatSalesValue(item.rate)}</td>
                                                                                                    </tr>
                                                                                                ))}
                                                                                            </tbody>
                                                                                        </table>
                                                                                    ) : (
                                                                                        <p className="text-gray-500 mb-3">No ingredients</p>
                                                                                    )}

                                                                                    {/* SUB RECIPES */}
                                                                                    <div className="text-left font-semibold mb-2 text-base">Sub Recipes</div>

                                                                                    {recipe.sub_recipes?.length > 0 ? (
                                                                                        <table className="min-w-full text-sm mb-4">
                                                                                            <thead>
                                                                                                <tr className="bg-gray-100">
                                                                                                    <th className="p-2">Name</th>
                                                                                                    <th className="p-2">Qty</th>
                                                                                                    <th className="p-2">UOM</th>
                                                                                                    <th className="p-2">Cost</th>
                                                                                                    <th className="p-2">Unit</th>
                                                                                                    <th className="p-2">Rate</th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {recipe.sub_recipes.map((sub, index) => (
                                                                                                    <tr key={index} className="border-t">
                                                                                                        <td className="p-2">{sub.name}</td>
                                                                                                        <td className="p-2">
                                                                                                            {convertToDisplayUnit(
                                                                                                                sub.quantity,
                                                                                                                sub.uom,
                                                                                                                sub.new_uom,
                                                                                                                sub.unit
                                                                                                            )}
                                                                                                        </td>
                                                                                                        <td className="p-2">{sub.new_uom || sub.uom}</td>
                                                                                                        <td className="p-2">{formatSalesValue(sub.costprice)}</td>
                                                                                                        <td className="p-2">{sub.unit} ({sub.uom})</td>
                                                                                                        <td className="p-2">{formatSalesValue(sub.rate)}</td>
                                                                                                    </tr>
                                                                                                ))}
                                                                                            </tbody>
                                                                                        </table>
                                                                                    ) : (
                                                                                        <p className="text-gray-500">No sub recipes</p>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </Fragment>
                                                                );
                                                            })}

                                                            {/* SPACER ROW between groups */}
                                                            {gi < groupEntries.length - 1 && (
                                                                <tr>
                                                                    <td colSpan={6} className="h-3 bg-white"></td>
                                                                </tr>
                                                            )}
                                                        </Fragment>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default ViewRecipeItems;